import React, { useState, useRef } from 'react';
import { useDocumentStore } from '../store/documentStore';
import { useProjectStore } from '../store/projectStore';
import { useNotificationStore } from '../store/notificationStore';
import { 
  DocumentIcon, 
  FolderIcon,
  TrashIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

export function Documents() {
  const { documents, addDocument, removeDocument } = useDocumentStore();
  const { projects } = useProjectStore();
  const { addNotification } = useNotificationStore();
  const [selectedProject, setSelectedProject] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const projectId = selectedProject ? parseInt(selectedProject) : undefined;
    const project = projectId ? projects.find(p => p.id === projectId) : undefined;

    addDocument({
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      uploadDate: new Date().toISOString().split('T')[0],
      projectId
    });

    addNotification({
      title: 'Document Uploaded',
      message: `${file.name} has been uploaded${project ? ` to ${project.name}` : ''}`,
      type: 'success'
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveDocument = (id: number, name: string) => {
    removeDocument(id);
    addNotification({
      title: 'Document Removed',
      message: `${name} has been removed`,
      type: 'info'
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <h1 className="text-2xl font-bold text-white mb-2">Documents</h1>
        <p className="text-gray-400">Manage project documents and files</p>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1 mr-4">
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full rounded-md border-gray-700 bg-gray-900 text-white shadow-sm focus:border-red-500 focus:ring-red-500 px-4 py-3"
            >
              <option value="">All Documents</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
            >
              <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
              Upload Document
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {documents
            .filter(doc => !selectedProject || doc.projectId === parseInt(selectedProject))
            .map((document) => (
              <div
                key={document.id}
                className="flex items-center justify-between p-4 bg-gray-750 rounded-lg"
              >
                <div className="flex items-center">
                  <DocumentIcon className="h-8 w-8 text-gray-400 mr-4" />
                  <div>
                    <h3 className="text-white font-medium">{document.name}</h3>
                    <div className="flex items-center text-sm text-gray-400 mt-1">
                      <span>{document.type}</span>
                      <span className="mx-2">•</span>
                      <span>{document.size}</span>
                      <span className="mx-2">•</span>
                      <span>{document.uploadDate}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  {document.projectId && (
                    <div className="flex items-center mr-4 text-sm text-gray-400">
                      <FolderIcon className="h-4 w-4 mr-1" />
                      {projects.find(p => p.id === document.projectId)?.name}
                    </div>
                  )}
                  <button
                    onClick={() => handleRemoveDocument(document.id, document.name)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          {documents.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <DocumentIcon className="h-12 w-12 mx-auto mb-4" />
              <p>No documents uploaded yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}